// Copyright (c) 2025 ParkEngage Inc. All rights reserved.

// This software is the confidential and proprietary information of
// ParkEngage Inc. ("Confidential Information"). You shall not
// disclose such Confidential Information and shall use it only in
// accordance with the terms of the license agreement you entered into
// with ParkEngage Inc.

// This source code is a valuable trade secret of ParkEngage Inc.

// THIS SOFTWARE IS PROVIDED BY ParkEngage Inc. ``AS IS'' AND ANY
// EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
// IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR
// PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL [YOUR COMPANY NAME] OR
// CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
// EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO,
// PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
// PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY
// OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
// (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
// OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.

// The views and conclusions contained in the software and documentation
// are those of the authors and should not be interpreted as representing
// official policies, either expressed or implied, of ParkEngage Inc.

import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { connect } from 'react-redux'
import './MultiSelectInput.scss'
// import { calculatedToolTip } from '../../Redux/Util/helpers'
import { useTooltips } from '../../../Hooks/useTooltips'
import { EMPTY_ARRAY } from '../../../constants/globalConstants'


const MultiSelectInput = ({
    options = [],
    selected = [],
    onSelectedChanged,
    displayValue = 'label',
    hasSelectAll = false,
    selectAllLabel = 'Select All',
    name,
    label,
    validation,
    required,
    noParentMargin,
    disableSearch = false,
    disabled = false,
    overrideStrings = {
        selectSomeItems: 'Select Some Items',
        allItemsAreSelected: 'All Items Are Selected',
    }
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [filteredOptions, setFilteredOptions] = useState(options)
    const [selectedOptions, setSelectedOptions] = useState(selected)
    const [searchTerm, setSearchTerm] = useState('')
    const dropdownRef = useRef(null)
    useTooltips(isOpen) // 👈 This enables the tooltip on dropdown open

    useEffect(() => {
        setFilteredOptions(options)
        setSelectedOptions(selected)
    }, [options, selected])

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
                setSearchTerm('')
                setFilteredOptions(options)
                // calculatedToolTip(false)
            }
        }

        document.body.addEventListener('click', handleOutsideClick)
        return () => {
            document.body.removeEventListener('click', handleOutsideClick)
        }
    }, [options])

    const toggleOption = (option = null, type = '') => {
        if (type === 'all') {
            const newSelected = selectedOptions.length === filteredOptions.length ? [] : [...filteredOptions]
            setSelectedOptions(newSelected)
            onSelectedChanged && onSelectedChanged(newSelected)
        } else {
            const exists = selectedOptions.find(sel => sel[displayValue] === option[displayValue])
            const newSelected = exists
                ? selectedOptions.filter(sel => sel[displayValue] !== option[displayValue])
                : [...selectedOptions, option]
            setSelectedOptions(newSelected)
            onSelectedChanged && onSelectedChanged(newSelected, option)
        }
    }

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase()
        setSearchTerm(term)
        const filtered = options.filter(opt =>
            String(opt[displayValue] || '').toLowerCase().includes(term)
        )
        setFilteredOptions(filtered)
    }

    const renderSelectedText = () => {
        const noneSelected = selectedOptions.length === 0
        const allSelected = selectedOptions.length === options.length

        if (noneSelected) return overrideStrings.selectSomeItems
        if (allSelected) return overrideStrings.allItemsAreSelected

        // Use function or fallback
        if (typeof overrideStrings.countSelected === 'function') {
            return overrideStrings.countSelected(selectedOptions.length)
        } else {
            return `(${selectedOptions.length} selected)` // fallback default
        }
    }


    return (
        <div className={`${noParentMargin ? 'mt-0' : ''} form-group user-actions facility-actions`}>
            {label && (
                <label htmlFor={name}>
                    {label} <span style={{ color: 'red' }}>{(validation || required) ? '*' : ''}</span>
                </label>
            )}
            <div
                className="multiselect-container toolTipSelectDropdown"
                onClick={e => e.stopPropagation()}
                ref={dropdownRef}
            >
                <div
                    className='form-control overFlowBlock align-items-center justify-content-between d-flex'
                    onClick={() => {
                        setIsOpen(!isOpen)
                        setSearchTerm('')
                        setFilteredOptions(options)
                        // calculatedToolTip(!isOpen)
                    }}
                >
                    <span className='wrapText'>{renderSelectedText()}</span>
                    <i className='fa fa-chevron-down'></i>
                </div>

                <div className={`options-container ${isOpen && !disabled ? 'open' : ''}`} onClick={e => e.stopPropagation()}>
                    {!disableSearch && (
                        <div className='searchWrap'>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={handleSearch}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}

                    <div className="my-2 scroll-container">
                        <ul className='nav flex-column'>
                            {hasSelectAll && searchTerm === '' && (
                                <li className="nav-item hovered px-1 m-0" onClick={e => e.stopPropagation()}>
                                    <label htmlFor='selectAll' className='nav-link p-0'>
                                        <input
                                            type="checkbox"
                                            id='selectAll'
                                            name='selectAll'
                                            className="select-all-checkbox mx-1"
                                            checked={selectedOptions.length === filteredOptions.length}
                                            onChange={() => toggleOption(null, 'all')}
                                        />
                                        {selectAllLabel}
                                    </label>
                                </li>
                            )}
                            {filteredOptions.map((option, index) => {
                                const isSelected = selectedOptions.some(sel => sel[displayValue] === option[displayValue])
                                return (
                                    <li key={index} className='nav-item overflow-hidden px-1 m-0' onClick={e => e.stopPropagation()}>
                                        <label
                                            data-tip={option.tooltip || ''}
                                            htmlFor={`${index}-single_option-${option[displayValue] || ''}`}
                                            className='nav-link p-0'
                                        >
                                            <input
                                                type="checkbox"
                                                id={`${index}-single_option-${option[displayValue] || ''}`}
                                                className='mx-1'
                                                checked={isSelected}
                                                onChange={() => toggleOption(option)}
                                            />
                                            {option[displayValue] || '-'}
                                        </label>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    )
}

MultiSelectInput.propTypes = {
    dispatch: PropTypes.func,
    options: PropTypes.array,
    selected: PropTypes.array,
    onSelectedChanged: PropTypes.func,
    overrideStrings: PropTypes.object,
    disableSearch: PropTypes.bool,
    disabled: PropTypes.bool,
    hasSelectAll: PropTypes.bool,
    selectAllLabel: PropTypes.string,
    noParentMargin: PropTypes.bool,
    label: PropTypes.string,
    validation: PropTypes.string,
    required: PropTypes.bool,
    name: PropTypes.string,
    displayValue: PropTypes.string
}

MultiSelectInput.defaulProps = {
    dispatch: noop,
    options: EMPTY_ARRAY,
    selected: EMPTY_ARRAY,
    onSelectedChanged: noop,
    overrideStrings: {},
    disableSearch: false,
    disabled: false,
    hasSelectAll: false,
    selectAllLabel: '',
    noParentMargin: false,
    label: '',
    validation: '',
    required: false,
    name: '',
    displayValue: 'label'
}

function mapStateToProps() {
    return {}
}
export default connect(mapStateToProps)(MultiSelectInput)